/**
 * Record despatch on a purchase order (docs/features/brandhub-order-routing/
 * 07_fulfilment_routes_update_spec.md §4): the ⋯ menu's dialog, in the style
 * of Record cancellation. The date the supplier despatched it (today by
 * default, never later), the carrier and the tracking reference. The carrier
 * is free text; the carriers this supplier's earlier purchase orders went with
 * are offered as one-click suggestions. The route it posts to, and the
 * suggestions, are the purchase order view's `record_despatch`.
 *
 * A line going to several places asks how many went to each (order to
 * invoice doc 102 §9.4), each defaulting to what is still outstanding there;
 * the dialog then posts every listed line with its destinations, leaving out
 * any with nothing going this time. With no such line it posts none, and each
 * open line goes whole to its one destination.
 *
 * The server's refusal, bound to the field it names, replaces the checks made
 * as typed; `onRecord` resolves to the refusal to show, or null once done.
 */
import { useId, useState } from "react";
import type {
  RecordDespatch,
  RecordDespatchLine,
  RecordPurchaseOrderDespatchRequest,
} from "@shared-core/data/order-to-invoice-types";
import {
  londonTodayIso,
  type OrderStepFailure,
} from "@shared-core/domain/order-to-invoice-presentation";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { Field, FailureBanner } from "./OrderDialogs";

/** A destination's place in the dialog: its line and delivery line. */
const keyOf = (lineId: string, deliveryLineId: string) =>
  `${lineId}|${deliveryLineId}`;

/** The checks made as typed on one destination: a whole number, no more than is outstanding there. */
export function destinationQuantityProblem(
  quantity: string,
  outstanding: number,
): string | undefined {
  const parsed = Number(quantity);
  if (quantity.trim() === "" || !Number.isInteger(parsed) || parsed < 0)
    return "Enter a whole number, 0 or more.";
  if (parsed > outstanding)
    return `Only ${outstanding.toLocaleString("en-GB")} are outstanding.`;
  return undefined;
}

/** The `lines` to post: every listed line with how many went where, leaving out the lines with nothing going. */
export function despatchLinesOf(
  lines: RecordDespatchLine[],
  quantities: Record<string, string>,
): RecordPurchaseOrderDespatchRequest["lines"] {
  return lines
    .map((line) => ({
      purchase_order_line_id: line.purchase_order_line_id,
      destinations: line.destinations.map((d) => ({
        destination_ref: d.delivery_line_id,
        quantity: Number(
          quantities[keyOf(line.purchase_order_line_id, d.delivery_line_id)],
        ),
      })),
    }))
    .filter((line) => line.destinations.some((d) => d.quantity > 0));
}

export function RecordDespatchDialog({
  purchaseOrderNo,
  supplierName,
  despatch,
  busy,
  onClose,
  onRecord,
}: {
  purchaseOrderNo: string;
  supplierName: string;
  despatch: RecordDespatch;
  busy: boolean;
  onClose: () => void;
  onRecord: (
    input: RecordPurchaseOrderDespatchRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const today = londonTodayIso();
  const [on, setOn] = useState(today);
  const [carrier, setCarrier] = useState("");
  const [trackingReference, setTrackingReference] = useState("");
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  // The lines as last posted, in order: the server names a refused one by its position there.
  const [postedLineIds, setPostedLineIds] = useState<string[]>([]);
  const onError =
    failure?.fieldErrors.despatched_on ??
    (touched && !on
      ? "Enter the day it was despatched."
      : touched && on > today
        ? "The despatch date can't be after today: record it once it happens."
        : undefined);
  const suggestions = despatch.carrier_suggestions;
  const lines = despatch.lines ?? [];
  const isPerDestination = lines.some((l) => l.destinations.length > 1);
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      lines.flatMap((line) =>
        line.destinations.map((d) => [
          keyOf(line.purchase_order_line_id, d.delivery_line_id),
          String(d.outstanding_quantity),
        ]),
      ),
    ),
  );
  const quantityProblems: Record<string, string | undefined> = isPerDestination
    ? Object.fromEntries(
        lines.flatMap((line) =>
          line.destinations.map((d) => {
            const key = keyOf(line.purchase_order_line_id, d.delivery_line_id);
            return [
              key,
              destinationQuantityProblem(
                quantities[key] ?? "",
                d.outstanding_quantity,
              ),
            ];
          }),
        ),
      )
    : {};
  const hasQuantityProblem = Object.values(quantityProblems).some(Boolean);
  const nothingGoing =
    isPerDestination &&
    !hasQuantityProblem &&
    despatchLinesOf(lines, quantities)!.length === 0
      ? "Say how many went to each address."
      : undefined;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record {purchaseOrderNo} as despatched</DialogTitle>
          <DialogDescription>
            {`When ${supplierName} has sent it. Its lines show as despatched, the purchase ledger gets the date, and it is expected the next working day.`}
          </DialogDescription>
        </DialogHeader>
        {/* noValidate: the date's own check says why beside it, where the browser's `max` would only block. */}
        <form
          noValidate
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            if (!on || on > today || hasQuantityProblem || nothingGoing) return;
            const posted = isPerDestination
              ? despatchLinesOf(lines, quantities)
              : undefined;
            setPostedLineIds(
              (posted ?? []).map((line) => line.purchase_order_line_id),
            );
            const refused = await onRecord({
              despatched_on: on,
              carrier: carrier.trim() || null,
              tracking_reference: trackingReference.trim() || null,
              ...(posted ? { lines: posted } : {}),
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field id={`${id}-on`} label="Despatched on" error={onError}>
            {(aria) => (
              <Input
                {...aria}
                type="date"
                max={today}
                value={on}
                onChange={(e) => setOn(e.target.value)}
              />
            )}
          </Field>
          <Field
            id={`${id}-carrier`}
            label="Carrier"
            error={failure?.fieldErrors.carrier}
            hint={
              suggestions.length > 0 ? (
                <span className="flex flex-wrap items-center gap-1">
                  <span>Used before:</span>
                  {suggestions.map((name) => (
                    <Button
                      key={name}
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      aria-label={`Use carrier ${name}`}
                      onClick={() => setCarrier(name)}
                    >
                      {name}
                    </Button>
                  ))}
                </span>
              ) : undefined
            }
          >
            {(aria) => (
              <Input
                {...aria}
                value={carrier}
                placeholder="DPD"
                maxLength={100}
                onChange={(e) => setCarrier(e.target.value)}
              />
            )}
          </Field>
          <Field
            id={`${id}-tracking`}
            label="Tracking reference"
            error={failure?.fieldErrors.tracking_reference}
          >
            {(aria) => (
              <Input
                {...aria}
                value={trackingReference}
                placeholder="15501234567890"
                maxLength={200}
                onChange={(e) => setTrackingReference(e.target.value)}
              />
            )}
          </Field>
          {isPerDestination && (
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>Going to</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">This despatch</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.flatMap((line) =>
                  line.destinations.map((d, position) => {
                    const key = keyOf(
                      line.purchase_order_line_id,
                      d.delivery_line_id,
                    );
                    const posted = postedLineIds.indexOf(
                      line.purchase_order_line_id,
                    );
                    // The server's refusal of this destination, or of the line's split (on its first row).
                    const refusal =
                      posted < 0
                        ? undefined
                        : (failure?.fieldErrors[
                            `lines.${posted}.destinations.${position}.quantity`
                          ] ??
                          (position === 0
                            ? failure?.fieldErrors[
                                `lines.${posted}.destinations`
                              ]
                            : undefined));
                    const problem = quantityProblems[key] ?? refusal;
                    const inputId = `${id}-${key}`;
                    const name =
                      lines.length > 1 ? `${line.label} · ${d.name}` : d.name;
                    return (
                      <TableRow key={key}>
                        <TableCell className="whitespace-normal">
                          {name}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {d.outstanding_quantity.toLocaleString("en-GB")}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            id={inputId}
                            inputMode="numeric"
                            className="ml-auto h-8 w-24"
                            aria-label={`How many went to ${name}`}
                            value={quantities[key] ?? ""}
                            onChange={(e) =>
                              setQuantities((current) => ({
                                ...current,
                                [key]: e.target.value,
                              }))
                            }
                            aria-invalid={problem ? true : undefined}
                            aria-describedby={
                              problem ? `${inputId}-error` : undefined
                            }
                          />
                          {problem && (
                            <p
                              id={`${inputId}-error`}
                              className="text-sm text-red-600"
                            >
                              {problem}
                            </p>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }),
                )}
              </TableBody>
            </Table>
          )}
          {nothingGoing && (
            <p role="alert" className="text-sm text-red-600">
              {nothingGoing}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button
              type="submit"
              disabled={busy || hasQuantityProblem || !!nothingGoing}
            >
              Record despatch
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
